import { Box, Button, SvgIcon } from '@mui/material';
import AgentBuilderShell from 'components/@home/@settings/Tabs/Agents/AgentBuilderShell';
import { AgentEditContext } from 'components/@home/@settings/Tabs/Agents/AgentEditContext';
import PagesFlowShell from 'components/@home/@settings/Tabs/Agents/PagesFlowShell';
import ChatFlowContextHeader from 'components/@home/@settings/Tabs/ChatFlowContextHeader';
import RightPane from 'components/@home/@settings/Tabs/RightPane';
import HomeOutlineIcon from 'mdi-react/HomeOutlineIcon';
import PlayOutlineIcon from 'mdi-react/PlayOutlineIcon';
import { func, object } from 'prop-types';
import React, { useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ChatFlowContext } from 'components/@home/@settings/Tabs/ChatFlowContext';
import { I18n } from 'utils/i18n';
import { buildAgenticPath, useAgenticRoute } from 'utils/agenticRoute';
import connector from 'components/@home/@settings/Tabs/connector';

const SECTION_LABELS = {
  overview: 'NavSettings',
  instructions: 'NavInstructions',
  skills: 'NavSkills',
  tools: 'NavTools',
  knowledge: 'NavKnowledge',
  tags: 'NavTags',
  metrics: 'NavMetrics',
};

const AgentWorkspace = ({
  flow,
  agentState = null,
  onBackToList,
  chatFlows,
  chatFlowEntityTypes,
  chatFlowIntents,
  chatFlowIntegrations,
  onSaveAgent = null,
  onSaveFlowColor = null,
  onTest = null,
  onCreateSkill = null,
  onCreateTool = null,
  onCreateKnowledge = null,
  onReloadTags = null,
  onReloadMetrics = null,
  onReloadKnowledge = null,
  onReloadSkills = null,
  authCompany = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { context } = useContext(ChatFlowContext);
  const isAgentic = flow?.mode === 'agent';
  const route = useAgenticRoute(authCompany);
  const fromSkillId = route.toolId ? location.state?.fromSkillId || null : null;

  const defaultSection = isAgentic ? 'instructions' : 'pages';
  const activeSection = route.section ?? defaultSection;

  useEffect(() => {
    if (!flow?._id) return;
    if (route.section) return;
    navigate(
      buildAgenticPath(authCompany, {
        flowId: flow._id,
        section: defaultSection,
        instructionKind: defaultSection === 'instructions' ? 'base' : undefined,
      }),
      { replace: true },
    );
  }, [flow?._id, route.section, defaultSection, navigate, authCompany]);

  const { agent, skills, tools, knowledge, tags, metrics } = agentState || {};

  const agentEditValue = useMemo(
    () => ({
      agent,
      skills,
      tools,
      knowledge,
      tags,
      metrics,
      flow,
      authCompany,
      onSaveAgent,
      onSaveFlowColor,
      onCreateSkill,
      onCreateTool,
      onCreateKnowledge,
      onReloadTags,
      onReloadMetrics,
      onReloadKnowledge,
      onReloadSkills,
    }),
    [
      agent,
      skills,
      tools,
      knowledge,
      tags,
      metrics,
      flow,
      authCompany,
      onSaveAgent,
      onSaveFlowColor,
      onCreateSkill,
      onCreateTool,
      onCreateKnowledge,
      onReloadTags,
      onReloadMetrics,
      onReloadKnowledge,
      onReloadSkills,
    ],
  );

  const breadcrumbs = useMemo(() => {
    const items = [
      {
        key: 'home',
        icon: <HomeOutlineIcon size={14} />,
        onClick: onBackToList,
        title: I18n.t('AgenticChatFlow.AgentsTitle'),
        ariaLabel: I18n.t('AgenticChatFlow.AgentsTitle'),
      },
      {
        key: 'agent',
        label: agent?.name || flow?.name,
        onClick: context?.clear ? () => context.clear() : undefined,
      },
    ];
    if (isAgentic && activeSection) {
      const sectionForBreadcrumb = fromSkillId ? 'skills' : activeSection;
      items.push({
        key: 'section',
        label: I18n.t(`AgenticChatFlow.${SECTION_LABELS[sectionForBreadcrumb] || 'NavOverview'}`),
        onClick:
          flow?._id && authCompany
            ? () =>
                navigate(
                  buildAgenticPath(authCompany, {
                    flowId: flow._id,
                    section: sectionForBreadcrumb,
                  }),
                )
            : undefined,
      });
    }
    if (route.skillId) {
      const skill = skills?.find(s => String(s._id) === String(route.skillId));
      items.push({ key: 'skill', label: skill?.displayName || skill?.name || '…' });
    }
    if (route.toolId && fromSkillId) {
      const skill = skills?.find(s => String(s._id) === String(fromSkillId));
      items.push({
        key: 'via-skill',
        label: skill?.displayName || skill?.name || '…',
        onClick:
          flow?._id && authCompany
            ? () =>
                navigate(
                  buildAgenticPath(authCompany, {
                    flowId: flow._id,
                    section: 'skills',
                    skillId: fromSkillId,
                  }),
                )
            : undefined,
      });
    }
    if (route.toolId) {
      const tool = tools?.find(t => String(t._id) === String(route.toolId));
      items.push({ key: 'tool', label: tool?.name || '…' });
    }
    if (route.knowledgeId) {
      const k = knowledge?.find(item => String(item._id) === String(route.knowledgeId));
      items.push({ key: 'knowledge', label: k?.displayName || k?.name || '…' });
    }
    return items;
  }, [
    agent,
    flow,
    authCompany,
    isAgentic,
    activeSection,
    route.skillId,
    route.toolId,
    route.knowledgeId,
    fromSkillId,
    context,
    skills,
    tools,
    knowledge,
    onBackToList,
    navigate,
  ]);

  const inlineSkillOrTool = Boolean(route.skillId || route.toolId || route.knowledgeId);
  const renderAgentInline = isAgentic && agent && inlineSkillOrTool;
  // Pages flows keep the canvas visible beside the step editor (classical chatflow UX).
  const fullWidthRightPane = isAgentic && !renderAgentInline && Boolean(context?.current);

  const headerActions =
    isAgentic && agent ? (
      <Button
        variant="outlined"
        color="secondary"
        size="small"
        startIcon={
          <SvgIcon sx={{ color: 'primary.main' }}>
            <PlayOutlineIcon />
          </SvgIcon>
        }
        onClick={onTest}
      >
        {I18n.t('AgenticChatFlow.TestInChat')}
      </Button>
    ) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 0',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <ChatFlowContextHeader breadcrumbs={breadcrumbs} actions={headerActions} />
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          flexDirection: 'row',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            ...(fullWidthRightPane ? { display: 'none' } : {}),
          }}
        >
          {isAgentic && agent ? (
            <AgentEditContext.Provider value={agentEditValue}>
              <AgentBuilderShell />
            </AgentEditContext.Provider>
          ) : (
            <PagesFlowShell
              flow={flow}
              chatFlowEntityTypes={chatFlowEntityTypes}
              chatFlowIntents={chatFlowIntents}
              chatFlowIntegrations={chatFlowIntegrations}
            />
          )}
        </Box>
        {context?.current && !renderAgentInline && (
          <Box
            sx={{
              width: '42%',
              maxWidth: 560,
              minWidth: 320,
              minHeight: 0,
              flexShrink: 0,
              borderLeft: 1,
              borderColor: 'divider',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              ...(fullWidthRightPane
                ? { flex: 1, width: 'auto', maxWidth: 'none', minWidth: 0 }
                : {}),
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <RightPane
                context={context}
                chatFlows={chatFlows}
                chatFlowEntityTypes={chatFlowEntityTypes}
                chatFlowIntents={chatFlowIntents}
                chatFlowIntegrations={chatFlowIntegrations}
                selectedFlow={flow?._id}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

AgentWorkspace.propTypes = {
  flow: object.isRequired,
  agentState: object,
  onBackToList: func.isRequired,
  chatFlows: object.isRequired,
  chatFlowEntityTypes: object.isRequired,
  chatFlowIntents: object.isRequired,
  chatFlowIntegrations: object.isRequired,
  onSaveAgent: func,
  onSaveFlowColor: func,
  onTest: func,
  onCreateSkill: func,
  onCreateTool: func,
  onCreateKnowledge: func,
  onReloadTags: func,
  onReloadMetrics: func,
  onReloadKnowledge: func,
  onReloadSkills: func,
  authCompany: object,
};

export default connector(AgentWorkspace);
